import { InventoryItemCollectionProps } from "./InventoryItemCollection";
import InventoryItem from "../../../../utils/game/mechanics/InventoryItem";
import InventoryItemIconSlot from "../InventoryItemSlot/InventoryItemIconSlot";
import InventoryItemTransferContext from "../../../../utils/sys/InventoryItemTransferContext";
import FlexRow from "../../../../common/components/layout/Flexbox/FlexRow";
import {
    executeTransfers,
    validateTransfer,
} from "../../../../utils/sys/InventoryItemTransferUtils";
import { useContext, useRef } from "react";

import "./style/InventoryItemGrid_style.css";
import { UIInventoryItemCollectionProps } from "./InventoryItemCollection";

type Props = UIInventoryItemCollectionProps & {
    onItemEditRequested?: (itemIndex: number) => void;
    onItemChanged?: (
        item: InventoryItem | null,
        index: number,
        mode: "insert" | "replace",
        isInSequence: boolean
    ) => void;
    onItemsReordered?: (reorderedItems: Array<InventoryItem>) => void;
    onTransferSequenceComplete?: () => void;
};

export default function InventoryItemGrid({
    id,
    items,
    isEditable,
    itemRestrictor,
    onItemEditRequested,
    onItemChanged,
    onItemsReordered,
    onTransferSequenceComplete,
}: Props): JSX.Element {
    const ref = useRef<HTMLDivElement>(null);

    const { transferTargetItemContainer, setTransferTargetItemContainer } =
        useContext(InventoryItemTransferContext);

    const inventoryItemTransferContext = useContext(
        InventoryItemTransferContext
    );

    return (
        <FlexRow
            forwardRef={ref}
            className="inventory-item-grid-container"
            wrap
        >
            {items.map((item, index) => {
                const slotID = `${id}-${index}`;

                return (
                    <InventoryItemIconSlot
                        id={slotID}
                        collectionID={id}
                        key={`slotID-${index}`}
                        className="item-slot"
                        item={item}
                        tooltipEnabled={true}
                        isEditable={isEditable}
                        itemRestrictor={itemRestrictor}
                        transferVariables={{
                            setItemCallback: (item) => {
                                onItemChanged?.(item, index, "replace", true);
                            },
                            onTransferSequenceComplete:
                                onTransferSequenceComplete,
                        }}
                        onClick={(payload) => {
                            if (isEditable && payload.target != null) {
                                if (transferTargetItemContainer == null) {
                                    setTransferTargetItemContainer(
                                        payload.target
                                    );
                                } else {
                                    if (
                                        transferTargetItemContainer.id ===
                                        slotID
                                    ) {
                                        // Initial item selection
                                        setTransferTargetItemContainer(null);
                                    } else {
                                        if (
                                            payload.target?.collectionID !=
                                                null &&
                                            payload.target.collectionID ===
                                                transferTargetItemContainer.collectionID
                                        ) {
                                            // Internal item swap
                                            if (
                                                payload.target.item != null &&
                                                transferTargetItemContainer.item !=
                                                    null
                                            ) {
                                                const item1 =
                                                    payload.target.item;
                                                const item2 =
                                                    transferTargetItemContainer.item;
                                                const index1 =
                                                    items.indexOf(item1);
                                                const index2 =
                                                    items.indexOf(item2);

                                                if (
                                                    index1 >= 0 &&
                                                    index2 >= 0
                                                ) {
                                                    onItemsReordered?.(
                                                        items.map(
                                                            (item, index) =>
                                                                index === index1
                                                                    ? item2
                                                                    : index ===
                                                                      index2
                                                                    ? item1
                                                                    : item
                                                        )
                                                    );
                                                }
                                            }
                                        } else {
                                            // Container-to-container transfer
                                            if (
                                                validateTransfer(
                                                    transferTargetItemContainer,
                                                    payload.target
                                                )
                                            ) {
                                                executeTransfers({
                                                    transfers: [
                                                        {
                                                            item: transferTargetItemContainer.item,
                                                            setItemCallback:
                                                                payload.target
                                                                    .transferVariables
                                                                    ?.setItemCallback,
                                                        },
                                                        {
                                                            item: payload.target
                                                                .item,
                                                            setItemCallback:
                                                                transferTargetItemContainer
                                                                    .transferVariables
                                                                    ?.setItemCallback,
                                                        },
                                                    ],
                                                    onComplete: () => {
                                                        onTransferSequenceComplete?.();

                                                        if (
                                                            transferTargetItemContainer
                                                                .transferVariables
                                                                ?.onTransferSequenceComplete !=
                                                                null &&
                                                            transferTargetItemContainer
                                                                .transferVariables
                                                                ?.onTransferSequenceComplete !==
                                                                onTransferSequenceComplete
                                                        ) {
                                                            transferTargetItemContainer.transferVariables.onTransferSequenceComplete();
                                                        }
                                                    },
                                                });
                                            }
                                        }

                                        setTransferTargetItemContainer(null);
                                    }
                                }
                            }
                        }}
                        isSelected={
                            inventoryItemTransferContext
                                .transferTargetItemContainer?.id !== null &&
                            inventoryItemTransferContext
                                .transferTargetItemContainer?.id === slotID
                        }
                        onLongClick={() => {
                            if (isEditable) {
                                onItemEditRequested?.(index);
                            }
                        }}
                    />
                );
            })}
        </FlexRow>
    );
}
